import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import UiSurface from '../components/UiSurface';
import {
  MemberMobileShell,
  MemberMobileHero,
} from '../components/MemberMobileShell';
import { useToast } from '../components/Toast';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';
import { useConfirmDialog } from '../components/ConfirmDialog';
import TableRowActions from '../components/TableRowActions';
import ActionFormModal from '../components/ActionFormModal';
import AssistanceNewCaseModal from '../components/AssistanceNewCaseModal';
import MechanismCategoryRowsEditor from '../components/MechanismCategoryRowsEditor';
import MechanismEligibilityEditor from '../components/MechanismEligibilityEditor';
import MmSelect from '../components/MmSelect';
import { useSettings } from '../contexts/SettingsContext';
import { assistanceAPI, filesPlatformAPI, ledgerPlatformAPI, membersAPI, peoplePlatformAPI, usersAPI } from '@/services';
import { isMemberLikeEntreaUser } from '../utils/iamRoles';
import { mapPeopleListResponse } from '../models/memberModel';
import {
  buildMechanismCategoryRows,
  defaultEligibilityForm,
  defaultMechanismCategoryRows,
  eligibilityFormFromMechanism,
  eligibilityPayloadFromForm,
  eventCategoryLabelKey,
  expandAssistanceCategories,
  formatEligibilitySummary,
  mechanismCategoriesPayloadFromRows,
  normalizeAssistanceCategoryCode,
  normalizeCategoryThresholds,
} from '../utils/assistanceEventCategories';

const statusOptions = ['active', 'paused', 'closed'];
const caseStatuses = ['draft', 'submitted', 'in_review', 'approved', 'rejected', 'paid', 'closed'];
const mechanismTypeOptions = ['assistance', 'solidarity', 'mutual'];

function caseStatusPillClass(status) {
  const st = String(status || '').toLowerCase();
  if (st === 'draft') return 'bg-slate-100 text-slate-800 ring-1 ring-slate-200';
  if (st === 'submitted' || st === 'in_review') return 'bg-amber-50 text-amber-950 ring-1 ring-amber-200';
  if (st === 'approved') return 'bg-[#edf4ff] text-[#113a5b] ring-1 ring-[#bcd4f6]';
  if (st === 'rejected') return 'bg-red-50 text-red-900 ring-1 ring-red-200';
  if (st === 'paid') return 'bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200';
  if (st === 'closed') return 'bg-slate-50 text-slate-600 ring-1 ring-slate-200';
  return 'bg-slate-100 text-slate-700 ring-1 ring-slate-200';
}

function formatMechanismCategoriesSummary(m, localeFmt, t) {
  const covered = expandAssistanceCategories(m?.covered_event_categories);
  if (!covered.length) return '—';
  const thr = Object.fromEntries(
    normalizeCategoryThresholds(m?.category_thresholds).map((r) => [r.category, r])
  );
  return covered
    .map((code) => {
      const label = t(`memberDashboard.eventCategory.${code}`, { defaultValue: code });
      const row = thr[code];
      if (!row || (!(row.min_amount > 0) && !(row.max_amount > 0))) return label;
      const min = Number(row.min_amount || 0);
      const max = Number(row.max_amount || 0);
      if (min > 0 && max > 0 && min === max) {
        return `${label} (${min.toLocaleString(localeFmt)} FBU)`;
      }
      if (min > 0 || max > 0) {
        return `${label} (${min.toLocaleString(localeFmt)}–${max.toLocaleString(localeFmt)})`;
      }
      return label;
    })
    .join(', ');
}

function formatMechanismName(name) {
  const raw = String(name || '').trim();
  if (!raw) return '';
  const n = raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ');
  if (n === 'assitance deces' || n === 'assistance deces') {
    return 'Assistance Décès';
  }
  return raw;
}

function formatMechanismType(type) {
  const raw = String(type || '').trim();
  if (!raw) return '';
  const k = raw.toLowerCase();
  if (k === 'assistance') return 'Assistance';
  if (k === 'solidarity') return 'Solidarity';
  if (k === 'mutual') return 'Mutual';
  return raw;
}

function formatCaseReference(c) {
  const direct = String(
    c?.case_number || c?.reference || c?.request_number || c?.ticket_number || ''
  ).trim();
  if (direct) return direct;
  const rawId = String(c?.id || '').trim();
  if (!rawId) return '—';
  return `AL-${rawId.slice(-8).toUpperCase()}`;
}

function formatAssistanceEventCategory(code, t) {
  const normalized = normalizeAssistanceCategoryCode(code);
  if (!normalized) return '—';
  return t(eventCategoryLabelKey(normalized), { defaultValue: normalized });
}

function formatAssistanceCaseMemberName(c, memberNameLookup) {
  const memberId = String(c?.member_profile_id || '').trim();
  const lookedUp = String(memberNameLookup[memberId] || '').trim();
  const notFound = lookedUp === 'Introuvable' || lookedUp === 'Not found';
  if (lookedUp && !notFound) return lookedUp;
  const stored = String(c?.member_name || '').trim();
  if (stored) return stored;
  return lookedUp || '';
}

function formatProfileDisplayName(profile) {
  const p = profile || {};
  const meta = p?.metadata?.profile || {};
  const last = String(p?.last_name || meta?.last_name || '').trim();
  const first = String(p?.first_name || meta?.first_name || '').trim();
  if (last && first) return `${last}, ${first}`;
  return String(p?.name || p?.full_name || '').trim();
}

function isLikelyMongoObjectId(id) {
  return /^[a-f0-9]{24}$/i.test(String(id || '').trim());
}

function toFriendlyAssistanceActionError(err, t) {
  const detail = String(err?.response?.data?.detail || '').trim();
  const lower = detail.toLowerCase();
  if (!detail) return t('assistanceManagement.errors.actionImpossible');

  if (lower.includes('approved_amount must be > 0')) {
    return t('assistanceManagement.errors.approvedGtZero');
  }
  if (lower.includes('paid_amount must be > 0')) {
    return t('assistanceManagement.errors.paidGtZero');
  }
  if (lower.includes('approved_amount exceeds mechanism max_amount')) {
    return t('assistanceManagement.errors.exceedsMechMax');
  }
  if (lower.includes('approved_amount is below mechanism min_amount')) {
    return t('assistanceManagement.errors.belowMechMin');
  }
  if (lower.includes('approved_amount exceeds category max_amount')) {
    return t('assistanceManagement.errors.exceedsCatMax');
  }
  if (lower.includes('approved_amount is below category min_amount')) {
    return t('assistanceManagement.errors.belowCatMin');
  }
  if (lower.includes('approved_amount exceeds configured max_amount')) {
    return t('assistanceManagement.errors.exceedsCfgMax');
  }
  if (lower.includes('approved_amount is below configured min_amount')) {
    return t('assistanceManagement.errors.belowCfgMin');
  }
  if (lower.includes('decision comment is required')) {
    return t('assistanceManagement.errors.decisionComment');
  }
  if (lower.includes('invalid status transition')) {
    return t('assistanceManagement.errors.invalidTransition');
  }
  if (lower.includes('assistance case not found')) {
    return t('assistanceManagement.errors.caseNotFound');
  }
  if (lower.includes('organization context is required')) {
    return t('assistanceManagement.errors.orgContext');
  }
  if (lower.includes('ledger transaction creation failed')) {
    return t('assistanceManagement.errors.ledgerTx');
  }
  if (
    lower.includes('payment_method is required') ||
    lower.includes('payment method is required') ||
    lower.includes('moyen de paiement')
  ) {
    return t('assistanceManagement.errors.paymentMethodRequired');
  }
  return detail;
}

function resolveLedgerPaymentMethodId(ledgerPaymentMethods, entreaKey) {
  const raw = String(entreaKey || '').trim().toLowerCase();
  if (!raw) return null;
  const kindMap = {
    banque: 'virement',
    bank: 'virement',
    virement: 'virement',
    especes: 'especes',
    espèces: 'especes',
    cheque: 'cheque',
    chèque: 'cheque',
    carte_bancaire: 'carte_bancaire',
    mobile_money: 'mobile_money',
    prelevement: 'prelevement',
    autre: 'autre',
  };
  const kind = kindMap[raw] || raw;
  const withAcc = (ledgerPaymentMethods || []).filter((pm) => pm?.ledger_account_id);
  const byKind = withAcc.find((pm) => String(pm.kind || '').toLowerCase() === kind);
  if (byKind) return byKind.id;
  const byName = withAcc.find((pm) => String(pm.name || '').toLowerCase().includes(raw));
  if (byName) return byName.id;
  const byId = withAcc.find((pm) => String(pm.id || '') === String(entreaKey || '').trim());
  if (byId) return byId.id;
  return null;
}

const AssistanceManagement = ({ forceStaffView = false }) => {
  const { success, error } = useToast();
  const { confirm } = useConfirmDialog();
  const { settings, refreshSettings } = useSettings();
  const { user } = useAuth();
  const { hasPermission, role } = usePermissions();
  const { t, i18n } = useTranslation('common');
  const localeFmt = i18n.language?.startsWith('en') ? 'en-US' : 'fr-FR';
  const labelMechanismStatus = useCallback(
    (s) => {
      const k = String(s || '').toLowerCase();
      if (k === 'active' || k === 'paused' || k === 'closed') {
        return t(`assistanceManagement.mechanismStatus.${k}`);
      }
      return s || '—';
    },
    [t]
  );
  const labelMechanismPeriodicity = useCallback(
    (value) => {
      const key = String(value || '').trim().toLowerCase();
      if (key === 'weekly') return t('assistanceManagement.periodicityWeekly');
      if (key === 'monthly') return t('assistanceManagement.periodicityMonthly');
      if (key === 'quarterly') return t('assistanceManagement.periodicityQuarterly');
      if (key === 'yearly') return t('assistanceManagement.periodicityYearly');
      return value || '—';
    },
    [t]
  );
  const labelCaseStatus = useCallback(
    (s) => {
      const k = String(s || '').toLowerCase();
      if (
        [
          'draft',
          'submitted',
          'in_review',
          'approved',
          'rejected',
          'paid',
          'closed',
          'cancelled',
        ].includes(k)
      ) {
        return t(`memberDashboard.caseStatus.${k}`);
      }
      return s || '—';
    },
    [t]
  );
  const labelMechanismType = useCallback(
    (value) => {
      const k = String(value || '').trim().toLowerCase();
      if (k === 'assistance') {
        return t('assistanceManagement.mechanismType.assistance', { defaultValue: 'Assistance' });
      }
      if (k === 'solidarity') {
        return t('assistanceManagement.mechanismType.solidarity', { defaultValue: 'Solidarite' });
      }
      if (k === 'mutual') {
        return t('assistanceManagement.mechanismType.mutual', { defaultValue: 'Mutuelle' });
      }
      return formatMechanismType(value);
    },
    [t]
  );
  const hasMemberProfileLink = !!(user?.member_id || user?.person_id);
  const isMembre = !forceStaffView && isMemberLikeEntreaUser(role, hasMemberProfileLink);
  /** Comme Mes cotisations : le JWT peut omettre person_id ; on le complète via l’API utilisateur IAM. */
  const currentMemberProfileId = String(user?.person_id || user?.member_id || '').trim();
  const [resolvedMemberProfileId, setResolvedMemberProfileId] = useState('');
  const [memberIdLookupDone, setMemberIdLookupDone] = useState(false);
  const myMemberProfileId = String(
    currentMemberProfileId || resolvedMemberProfileId || ''
  ).trim();
  const canReadStaff =
    !isMembre && (hasPermission('assistance.cases.read') || hasPermission('contributions.view'));
  const canReadMemberOwn = Boolean(isMembre && hasPermission('contributions.view_own'));
  const canReadOwnCases = canReadMemberOwn;
  const canRead = canReadStaff || canReadMemberOwn;
  const showGovernanceTab = canReadStaff;
  const canCreateMechanism = hasPermission('assistance.mechanisms.write') || hasPermission('settings.edit');
  const canCreateCase = hasPermission('assistance.cases.create') || hasPermission('contributions.create');
  const canCreateOwnCase = canCreateCase || canReadOwnCases;
  const canReview = hasPermission('assistance.cases.review') || hasPermission('contributions.validate');
  const canApprove = hasPermission('assistance.cases.approve') || hasPermission('contributions.approve');
  const canReject = hasPermission('assistance.cases.reject') || hasPermission('contributions.approve');
  const canPay = hasPermission('assistance.cases.pay') || hasPermission('financial.create');
  const canClose = canReview || canApprove || canReject || canPay;
  const canReadReports = hasPermission('assistance.reports.read') || hasPermission('dashboard.stats');
  const [tab, setTab] = useState('mechanisms');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [mechanisms, setMechanisms] = useState([]);
  const [cases, setCases] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseStatusFilter, setCaseStatusFilter] = useState('');
  const [caseMechanismFilter, setCaseMechanismFilter] = useState('');
  /** '' = toutes les allocations ; 'action' = soumis ou en examen (file d'instruction) */
  const [queueFilter, setQueueFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [actionModal, setActionModal] = useState({
    isOpen: false,
    mode: null, // approve|reject|paid
    target: null,
    amount: '',
    comment: '',
    name: '',
    payment_method: '',
    pendingFiles: [],
  });
  const [ledgerPaymentMethods, setLedgerPaymentMethods] = useState([]);
  const [ledgerPmLoading, setLedgerPmLoading] = useState(false);
  const [caseFileMeta, setCaseFileMeta] = useState({});
  const [showNewCaseModal, setShowNewCaseModal] = useState(false);
  /** Dossier en cours d'édition (membre) */
  const [editingCase, setEditingCase] = useState(null);
  const [showNewMechanismModal, setShowNewMechanismModal] = useState(false);
  const [showEditMechanismModal, setShowEditMechanismModal] = useState(false);
  const [editingMechanism, setEditingMechanism] = useState(null);
  /** Lookup ponctuel: member_profile_id -> nom lisible. */
  const [memberNameLookup, setMemberNameLookup] = useState({});
  const [memberLookupLoading, setMemberLookupLoading] = useState({});
  const memberResolveInFlightRef = useRef(new Set());
  const requireDecisionNote = settings?.assistance?.case_workflow?.require_decision_note !== false;
  const [mechanismForm, setMechanismForm] = useState({
    name: '',
    type: 'assistance',
    contribution_periodicity: 'monthly',
    status: 'active',
    eligibility: defaultEligibilityForm(),
    category_rows: defaultMechanismCategoryRows(),
  });
  const [mechanismEditForm, setMechanismEditForm] = useState({
    name: '',
    type: 'assistance',
    contribution_periodicity: 'monthly',
    status: 'active',
    eligibility: defaultEligibilityForm(),
    category_rows: defaultMechanismCategoryRows(),
  });

  const resetMechanismForm = () => {
    setMechanismForm({
      name: '',
      type: 'assistance',
      contribution_periodicity: 'monthly',
      status: 'active',
      eligibility: defaultEligibilityForm(),
      category_rows: defaultMechanismCategoryRows(),
    });
  };

  const openEditMechanismModal = (m) => {
    if (!m) return;
    setEditingMechanism(m);
    setMechanismEditForm({
      name: String(m.name || ''),
      type: String(m.type || 'assistance'),
      contribution_periodicity: String(m.contribution_periodicity || 'monthly'),
      status: String(m.status || 'active'),
      eligibility: eligibilityFormFromMechanism(m),
      category_rows: buildMechanismCategoryRows(m),
    });
    setShowEditMechanismModal(true);
  };

  const mechanismMap = useMemo(
    () => Object.fromEntries(mechanisms.map((m) => [String(m.id || ''), m])),
    [mechanisms]
  );
  const filteredCases = useMemo(() => {
    const q = String(searchText || '').trim().toLowerCase();
    return cases.filter((c) => {
      if (queueFilter === 'action') {
        const st = String(c.status || '').toLowerCase();
        if (!['submitted', 'in_review'].includes(st)) return false;
      }
      if (caseStatusFilter && c.status !== caseStatusFilter) return false;
      if (caseMechanismFilter && String(c.mechanism_id || '') !== caseMechanismFilter) return false;
      if (canReadOwnCases && myMemberProfileId) {
        if (String(c.member_profile_id || '').trim() !== myMemberProfileId) return false;
      }
      if (!q) return true;
      const memberId = String(c.member_profile_id || '').trim();
      const memberName = formatAssistanceCaseMemberName(c, memberNameLookup);
      const eventLabel = formatAssistanceEventCategory(c.event_category, t);
      return (
        String(c.id || '').toLowerCase().includes(q) ||
        String(c.reason || '').toLowerCase().includes(q) ||
        memberId.toLowerCase().includes(q) ||
        memberName.toLowerCase().includes(q) ||
        String(c.member_name || '').toLowerCase().includes(q) ||
        String(c.event_category || '').toLowerCase().includes(q) ||
        eventLabel.toLowerCase().includes(q)
      );
    });
  }, [
    cases,
    caseStatusFilter,
    caseMechanismFilter,
    queueFilter,
    searchText,
    canReadOwnCases,
    myMemberProfileId,
    memberNameLookup,
    t,
  ]);
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (String(searchText || '').trim()) count += 1;
    if (caseStatusFilter) count += 1;
    if (caseMechanismFilter) count += 1;
    if (queueFilter) count += 1;
    return count;
  }, [searchText, caseStatusFilter, caseMechanismFilter, queueFilter]);

  const instructionQueueCount = useMemo(
    () =>
      cases.filter((c) =>
        ['submitted', 'in_review'].includes(String(c.status || '').toLowerCase())
      ).length,
    [cases]
  );

  /** Synthèse vue membre (même grille que Mes cotisations). */
  const memberAllocationStats = useMemo(() => {
    if (!canReadMemberOwn) return null;
    const list = cases || [];
    const totalCount = list.length;
    let instruction = 0;
    let paidCount = 0;
    let rejectedCount = 0;
    let totalRequested = 0;
    let withFiles = 0;
    let totalReleased = 0;
    for (const c of list) {
      const st = String(c?.status || '').toLowerCase();
      if (st === 'submitted' || st === 'in_review') instruction += 1;
      if (st === 'paid') paidCount += 1;
      if (st === 'rejected') rejectedCount += 1;
      totalRequested += Number(c?.requested_amount || 0);
      if (Array.isArray(c?.supporting_file_ids) && c.supporting_file_ids.length > 0) withFiles += 1;
      if (st === 'closed') {
        const paid = Number(c?.paid_amount || 0);
        const appr = Number(c?.approved_amount || 0);
        totalReleased += paid > 0 ? paid : appr;
      } else if (st === 'paid') {
        totalReleased += Number(c?.paid_amount || 0);
      } else if (st === 'approved') {
        totalReleased += Number(c?.approved_amount || 0);
      }
    }
    return { totalCount, instruction, paidCount, rejectedCount, totalRequested, withFiles, totalReleased };
  }, [canReadMemberOwn, cases]);

  /** Vue membre : masquer l’historique (titre + filtres + tableau) si aucun dossier et aucun montant versé/approuvé. */
  const showMemberAllocationHistorique = useMemo(() => {
    if (!canReadMemberOwn) return true;
    const s = memberAllocationStats;
    if (!s) return false;
    return s.totalCount > 0 || Number(s.totalReleased || 0) > 0;
  }, [canReadMemberOwn, memberAllocationStats]);

  const caseBusinessStats = useMemo(() => {
    const uniqueMembers = new Set(
      cases.map((c) => String(c?.member_profile_id || '').trim()).filter(Boolean)
    );
    const totals = cases.reduce(
      (acc, c) => {
        const st = String(c?.status || '').toLowerCase();
        if (st === 'approved') acc.approvedCases += 1;
        if (st === 'paid') acc.paidCases += 1;
        if (st === 'rejected') acc.rejectedCases += 1;
        if (st === 'submitted' || st === 'in_review') acc.instructionCases += 1;
        acc.totalRequested += Number(c?.requested_amount || 0);
        acc.totalApproved += Number(c?.approved_amount || 0);
        acc.totalPaid += Number(c?.paid_amount || 0);
        return acc;
      },
      {
        approvedCases: 0,
        paidCases: 0,
        rejectedCases: 0,
        instructionCases: 0,
        totalRequested: 0,
        totalApproved: 0,
        totalPaid: 0,
      }
    );
    const expectedContributionAmount = Number(
      settings?.assistance?.financial_mechanics?.expected_contribution_amount || 0
    );
    const totalExpectedContributions = expectedContributionAmount * uniqueMembers.size;
    return {
      uniqueMembersCount: uniqueMembers.size,
      expectedContributionAmount,
      totalExpectedContributions,
      totalPaidContributions: totals.totalPaid,
      arrears: Math.max(0, totalExpectedContributions - totals.totalPaid),
      ...totals,
    };
  }, [cases, settings?.assistance?.financial_mechanics?.expected_contribution_amount]);

  const translateLedgerKind = useCallback(
    (kind) => {
      const raw = String(kind || '').trim().toLowerCase();
      const norm = raw === 'chèque' || raw === 'cheque' ? 'cheque' : raw;
      const key = `contributionsPage.ledgerKind.${norm}`;
      const out = t(key, { defaultValue: '' });
      if (out && out !== key) return out;
      return kind ? String(kind).replace(/_/g, ' ') : '';
    },
    [t]
  );

  const paymentMethodOptions = useMemo(() => {
    const raw = settings?.financial?.payment_methods;
    if (Array.isArray(raw) && raw.length > 0) {
      const seen = new Set();
      return raw
        .map((x) => String(x || '').trim())
        .filter(Boolean)
        .filter((v) => (seen.has(v) ? false : (seen.add(v), true)))
        .map((v) => ({
          value: v,
          label: v.charAt(0).toUpperCase() + v.slice(1).replace(/_/g, ' '),
        }));
    }
    return (ledgerPaymentMethods || [])
      .filter((pm) => pm?.ledger_account_id)
      .map((pm) => {
        const name = String(pm.name || '').trim();
        const kind = String(pm.kind || '').trim().toLowerCase();
        const value = name ? name.toLowerCase() : kind;
        const kindLabel = translateLedgerKind(kind) || (kind ? kind.replace(/_/g, ' ') : '');
        const label = name || kindLabel || t('contributionsPage.defaultPmLabel');
        return { value, label, id: pm.id };
      })
      .filter((row) => row.value);
  }, [settings?.financial?.payment_methods, ledgerPaymentMethods, t, translateLedgerKind]);

  const defaultPaymentMethod = useMemo(() => {
    const def = String(settings?.financial?.default_payment_method || '').trim();
    if (def && paymentMethodOptions.some((o) => o.value === def)) return def;
    return paymentMethodOptions[0]?.value || '';
  }, [settings?.financial?.default_payment_method, paymentMethodOptions]);

  const loadAll = async () => {
    try {
      setLoading(true);
      if (canReadOwnCases && !myMemberProfileId) {
        setMechanisms([]);
        setCases([]);
        setSummary(null);
        return;
      }
      const caseParams = { limit: 200, skip: 0 };
      if (canReadOwnCases && myMemberProfileId) {
        caseParams.member_profile_id = myMemberProfileId;
      }
      const shouldLoadMechanisms = !canReadOwnCases || canCreateOwnCase;
      const promises = [
        shouldLoadMechanisms
          ? assistanceAPI
              .getMechanisms({ limit: 200, skip: 0 })
              .catch(() => ({ data: { solidarity_mechanisms: [] } }))
          : Promise.resolve({ data: { solidarity_mechanisms: [] } }),
        assistanceAPI.getCases(caseParams),
        canReadReports ? assistanceAPI.getSummaryReport() : Promise.resolve({ data: null }),
      ];
      const [mRes, cRes, sRes] = await Promise.all(promises);
      setMechanisms(mRes?.data?.solidarity_mechanisms || []);
      const nextCases = cRes?.data?.assistance_cases || [];
      setCases(nextCases);
      setSummary(sRes?.data || null);
      setSelectedCase((prev) => {
        if (!prev?.id) return prev;
        const fresh = nextCases.find((c) => String(c?.id) === String(prev.id));
        return fresh || prev;
      });
    } catch (err) {
      console.error(err);
      error(err?.response?.data?.detail || t('assistanceManagement.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLedgerPmLoading(true);
      try {
        const res = await ledgerPlatformAPI.listPaymentMethods({ include_inactive: false, limit: 200 });
        if (!cancelled) {
          setLedgerPaymentMethods(res?.data?.payment_methods || []);
        }
      } catch (_e) {
        if (!cancelled) setLedgerPaymentMethods([]);
      } finally {
        if (!cancelled) setLedgerPmLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const ids = Array.isArray(selectedCase?.supporting_file_ids)
      ? selectedCase.supporting_file_ids.map((id) => String(id || '').trim()).filter(Boolean)
      : [];
    if (!ids.length) {
      setCaseFileMeta({});
      return undefined;
    }
    (async () => {
      const next = {};
      await Promise.all(
        ids.map(async (fid) => {
          try {
            const res = await filesPlatformAPI.get(fid);
            const data = res?.data || {};
            next[fid] = {
              name:
                String(data.filename || data.name || data.original_name || data.title || '').trim() ||
                fid,
              url:
                String(
                  data.url || data.download_url || data.file_url || data.public_url || ''
                ).trim() || '',
            };
          } catch {
            next[fid] = { name: fid, url: '' };
          }
        })
      );
      if (!cancelled) setCaseFileMeta(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCase?.id, selectedCase?.supporting_file_ids]);

  useEffect(() => {
    let cancelled = false;
    const done = (v) => {
      if (!cancelled) setMemberIdLookupDone(v);
    };

    if (!canReadOwnCases) {
      setResolvedMemberProfileId('');
      done(true);
      return () => {
        cancelled = true;
      };
    }

    if (currentMemberProfileId) {
      setResolvedMemberProfileId('');
      done(true);
      return () => {
        cancelled = true;
      };
    }

    done(false);
    setResolvedMemberProfileId('');

    (async () => {
      const userIdCandidates = [user?.id, user?.sub, user?.user_id]
        .map((v) => String(v || '').trim())
        .filter(Boolean);
      for (const uid of userIdCandidates) {
        if (cancelled) return;
        try {
          const res = await usersAPI.getById(uid);
          const pid = String(res?.data?.person_id || res?.data?.member_id || '').trim();
          if (pid) {
            if (!cancelled) setResolvedMemberProfileId(pid);
            done(true);
            return;
          }
        } catch (_err) {
          // Ignorer et essayer le candidat suivant.
        }
      }
      done(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [canReadOwnCases, currentMemberProfileId, user?.id, user?.sub, user?.user_id]);

  useEffect(() => {
    if (canRead) loadAll();
  }, [canRead, canReadOwnCases, myMemberProfileId]);

  useEffect(() => {
    if (canReadMemberOwn && !canReadStaff) setTab('cases');
  }, [canReadMemberOwn, canReadStaff]);

  useEffect(() => {
    if (!showGovernanceTab && tab === 'mechanisms') setTab('cases');
  }, [showGovernanceTab, tab]);

  const onCreateMechanism = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!String(mechanismForm.name || '').trim()) {
      error(t('assistanceManagement.mechanismNameRequired'));
      return;
    }
    const catPayload = mechanismCategoriesPayloadFromRows(mechanismForm.category_rows);
    if (!catPayload.covered_event_categories.length) {
      error(t('assistanceManagement.categoriesRequired'));
      return;
    }
    try {
      setSubmitting(true);
      await assistanceAPI.createMechanism({
        name: String(mechanismForm.name || '').trim(),
        type: String(mechanismForm.type || '').trim() || 'assistance',
        contribution_periodicity: mechanismForm.contribution_periodicity || 'monthly',
        status: mechanismForm.status || 'active',
        eligibility_rules: eligibilityPayloadFromForm(mechanismForm.eligibility),
        ...catPayload,
      });
      success(t('assistanceManagement.mechanismCreated'));
      resetMechanismForm();
      setShowNewMechanismModal(false);
      await loadAll();
    } catch (err) {
      console.error(err);
      error(err?.response?.data?.detail || t('assistanceManagement.mechanismCreateError'));
    } finally {
      setSubmitting(false);
    }
  };

  const openActionModal = (mode, target, defaults = {}) => {
    setActionModal({
      isOpen: true,
      mode,
      target,
      amount: defaults.amount || '',
      comment: defaults.comment || '',
      name: '',
      payment_method:
        mode === 'paid'
          ? String(defaults.payment_method || defaultPaymentMethod || '').trim()
          : '',
      pendingFiles: [],
    });
  };

  const closeActionModal = () => {
    setActionModal({
      isOpen: false,
      mode: null,
      target: null,
      amount: '',
      comment: '',
      name: '',
      payment_method: '',
      pendingFiles: [],
    });
  };

  const memberOwnsCase = (c) =>
    Boolean(
      canReadOwnCases &&
        myMemberProfileId &&
        String(c?.member_profile_id || '').trim() === String(myMemberProfileId),
    );

  const onEditMemberCase = (c) => {
    if (!memberOwnsCase(c)) return;
    const st = String(c?.status || '').toLowerCase();
    if (!['draft', 'submitted', 'in_review'].includes(st)) return;
    setEditingCase(c);
  };

  const onDeleteMemberCase = async (c) => {
    if (!memberOwnsCase(c) || String(c?.status || '').toLowerCase() !== 'draft') return;
    const ok = await confirm({
      title: t('assistanceManagement.deleteDraftTitle'),
      message: t('assistanceManagement.deleteDraftMessage', { id: c.id }),
      confirmText: t('assistanceManagement.confirmDelete'),
      cancelText: t('assistanceManagement.cancel'),
      type: 'danger',
    });
    if (!ok) return;
    try {
      setSubmitting(true);
      await assistanceAPI.deleteCase(c.id);
      success(t('assistanceManagement.draftDeleted'));
      if (String(selectedCase?.id || '') === String(c.id)) {
        setSelectedCase(null);
      }
      await loadAll();
    } catch (err) {
      console.error(err);
      error(
        err?.response?.data?.detail ||
          err?.message ||
          t('assistanceManagement.deleteFailed'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openCreateCaseModal = () => {
    setEditingCase(null);
    setShowNewCaseModal(true);
  };

  const onAction = async (c, action) => {
    try {
      setSubmitting(true);
      if (action === 'submit') await assistanceAPI.submitCase(c.id);
      if (action === 'close') {
        const ok = await confirm({
          title: t('assistanceManagement.closeCaseTitle'),
          message: t('assistanceManagement.closeCaseMessage', { id: c.id }),
          confirmText: t('assistanceManagement.closeCaseConfirm'),
          cancelText: t('assistanceManagement.cancel'),
          type: 'warning',
        });
        if (!ok) return;
        await assistanceAPI.closeCase(c.id);
      }
      success(t('assistanceManagement.actionOk'));
      await loadAll();
    } catch (err) {
      console.error(err);
      error(toFriendlyAssistanceActionError(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  const resolveMemberDisplayName = useCallback(
    async (memberProfileId) => {
      const pid = String(memberProfileId || '').trim();
      if (!pid) return;

      const cached = String(memberNameLookup[pid] || '').trim();
      if (cached) return;
      if (memberResolveInFlightRef.current.has(pid)) return;

      memberResolveInFlightRef.current.add(pid);
      setMemberLookupLoading((prev) => ({ ...prev, [pid]: true }));
      try {
        let displayName = '';

        if (isLikelyMongoObjectId(pid)) {
          try {
            const mRes = await membersAPI.getById(pid);
            displayName = formatProfileDisplayName(mRes?.data);
          } catch {
            // fallback ci-dessous
          }
        }

        if (!displayName && !isLikelyMongoObjectId(pid)) {
          try {
            const pRes = await peoplePlatformAPI.getById(pid);
            displayName = formatProfileDisplayName(pRes?.data);
          } catch {
            // fallback IAM ci-dessous
          }
        }

        if (!displayName) {
          try {
            const uRes = await usersAPI.getByMemberId(pid);
            const u = uRes?.data || {};
            displayName = String(
              u.name || [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || ''
            ).trim();
          } catch {
            // ignore
          }
        }

        setMemberNameLookup((prev) => {
          if (String(prev[pid] || '').trim()) return prev;
          return {
            ...prev,
            [pid]:
              displayName ||
              t('assistanceManagement.memberLookupNotFound', { defaultValue: 'Introuvable' }),
          };
        });
      } finally {
        memberResolveInFlightRef.current.delete(pid);
        setMemberLookupLoading((prev) => ({ ...prev, [pid]: false }));
      }
    },
    [memberNameLookup, t]
  );

  useEffect(() => {
    if (canReadOwnCases || !cases.length) return undefined;
    let cancelled = false;

    const preloadMemberNames = async () => {
      const next = {};
      try {
        const res = await peoplePlatformAPI.list({ limit: 2000, skip: 0 });
        if (cancelled) return;
        const members = mapPeopleListResponse(res?.data);
        for (const m of members) {
          const id = String(m?.id || '').trim();
          if (!id) continue;
          const last = String(m?.last_name || '').trim();
          const first = String(m?.first_name || '').trim();
          const label =
            last && first ? `${last}, ${first}` : String(m?.name || '').trim();
          if (label) next[id] = label;
        }
      } catch (err) {
        console.error(err);
      }

      if (Object.keys(next).length) {
        setMemberNameLookup((prev) => ({ ...prev, ...next }));
      }

      const ids = [
        ...new Set(
          cases.map((c) => String(c?.member_profile_id || '').trim()).filter(Boolean)
        ),
      ];

      for (const id of ids) {
        if (cancelled) break;
        if (next[id]) continue;
        if (memberResolveInFlightRef.current.has(id)) continue;

        let displayName = '';
        memberResolveInFlightRef.current.add(id);

        try {
          if (isLikelyMongoObjectId(id)) {
            try {
              const mRes = await membersAPI.getById(id);
              displayName = formatProfileDisplayName(mRes?.data);
            } catch {
              // ignore
            }
          }
          if (!displayName) {
            try {
              const uRes = await usersAPI.getByMemberId(id);
              const u = uRes?.data || {};
              displayName = String(
                u.name || [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || ''
              ).trim();
            } catch {
              // ignore
            }
          }
        } finally {
          memberResolveInFlightRef.current.delete(id);
        }

        if (cancelled) break;
        setMemberNameLookup((prev) => {
          if (String(prev[id] || '').trim()) return prev;
          if (!displayName) return prev;
          return { ...prev, [id]: displayName };
        });
      }
    };

    preloadMemberNames();
    return () => {
      cancelled = true;
    };
  }, [cases, canReadOwnCases]);

  const onUploadSupportingFiles = async (caseId, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    try {
      setSubmitting(true);
      const fileIds = [];
      for (const file of files) {
        const uploaded = await filesPlatformAPI.upload(file);
        const fid =
          uploaded?.data?.id ||
          uploaded?.data?.file_id ||
          uploaded?.data?.upload_id ||
          '';
        if (!fid) {
          throw new Error(t('assistanceManagement.fileIdMissing'));
        }
        fileIds.push(String(fid));
      }
      await assistanceAPI.attachFiles(caseId, fileIds);
      success(t('assistanceManagement.filesAdded'));
      await loadAll();
    } catch (err) {
      console.error(err);
      error(err?.response?.data?.detail || err?.message || t('assistanceManagement.uploadFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const onSetMechanismStatus = async (m, status) => {
    try {
      setSubmitting(true);
      await assistanceAPI.setMechanismStatus(m.id, status);
      success(t('assistanceManagement.mechanismStatusUpdated', { status: labelMechanismStatus(status) }));
      await loadAll();
    } catch (err) {
      console.error(err);
      error(err?.response?.data?.detail || t('assistanceManagement.mechanismStatusError'));
    } finally {
      setSubmitting(false);
    }
  };

  const onDeleteMechanism = async (m) => {
    const ok = await confirm({
      title: t('assistanceManagement.deleteMechanismTitle'),
      message: t('assistanceManagement.deleteMechanismMessage', { name: m?.name || m?.id || '' }),
      confirmText: t('assistanceManagement.confirmDelete'),
      cancelText: t('assistanceManagement.cancel'),
      type: 'danger',
    });
    if (!ok) return;
    try {
      setSubmitting(true);
      await assistanceAPI.deleteMechanism(m.id);
      success(t('assistanceManagement.mechanismDeleted'));
      await loadAll();
    } catch (err) {
      console.error(err);
      error(err?.response?.data?.detail || t('assistanceManagement.mechanismDeleteError'));
    } finally {
      setSubmitting(false);
    }
  };

  const onUpdateMechanism = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!editingMechanism?.id) return;
    if (!String(mechanismEditForm.name || '').trim()) {
      error(t('assistanceManagement.mechanismNameRequired'));
      return;
    }
    const catPayload = mechanismCategoriesPayloadFromRows(mechanismEditForm.category_rows);
    if (!catPayload.covered_event_categories.length) {
      error(t('assistanceManagement.categoriesRequired'));
      return;
    }
    try {
      setSubmitting(true);
      await assistanceAPI.patchMechanism(editingMechanism.id, {
        name: String(mechanismEditForm.name || '').trim(),
        type: String(mechanismEditForm.type || '').trim() || 'assistance',
        contribution_periodicity: mechanismEditForm.contribution_periodicity || 'monthly',
        status: mechanismEditForm.status || 'active',
        eligibility_rules: eligibilityPayloadFromForm(mechanismEditForm.eligibility),
        ...catPayload,
      });
      success(t('assistanceManagement.mechanismUpdated'));
      setShowEditMechanismModal(false);
      setEditingMechanism(null);
      await loadAll();
    } catch (err) {
      console.error(err);
      error(err?.response?.data?.detail || t('assistanceManagement.mechanismUpdateError'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitActionModal = async (e) => {
    e.preventDefault();
    const { mode, target } = actionModal;
    if (!target || !mode) return;
    const trimmedComment = String(actionModal.comment || '').trim();
    if ((mode === 'approve' || mode === 'reject') && requireDecisionNote && !trimmedComment) {
      error(t('assistanceManagement.decisionCommentRequired'));
      return;
    }
    try {
      setSubmitting(true);
      if (mode === 'approve') {
        await assistanceAPI.approveCase(target.id, {
          approved_amount: Number(actionModal.amount || 0),
          comment: trimmedComment,
        });
        success(t('assistanceManagement.caseApproved'));
        await loadAll();
      } else if (mode === 'reject') {
        await assistanceAPI.rejectCase(target.id, {
          comment: trimmedComment || t('assistanceManagement.rejectManual'),
        });
        success(t('assistanceManagement.caseRejected'));
        await loadAll();
      } else if (mode === 'paid') {
        const paymentMethod = String(actionModal.payment_method || '').trim();
        if (!paymentMethod) {
          error(t('assistanceManagement.errors.paymentMethodRequired'));
          setSubmitting(false);
          return;
        }
        const paymentMethodId = resolveLedgerPaymentMethodId(ledgerPaymentMethods, paymentMethod);
        const pendingFiles = Array.isArray(actionModal.pendingFiles) ? actionModal.pendingFiles : [];
        const proofFileIds = [];
        for (const file of pendingFiles) {
          if (Number(file?.size) > 10 * 1024 * 1024) {
            error(t('assistanceManagement.newCaseModal.fileTooLarge', { name: file?.name || 'fichier' }));
            setSubmitting(false);
            return;
          }
          const uploaded = await filesPlatformAPI.upload(file);
          const fid =
            uploaded?.data?.id ||
            uploaded?.data?.file_id ||
            uploaded?.data?.upload_id ||
            '';
          if (!fid) {
            throw new Error(t('assistanceManagement.fileIdMissing'));
          }
          proofFileIds.push(String(fid));
        }
        await assistanceAPI.markPaid(target.id, {
          paid_amount: Number(actionModal.amount || 0),
          payment_method: paymentMethod,
          ...(paymentMethodId ? { payment_method_id: paymentMethodId } : {}),
          ...(proofFileIds.length ? { supporting_file_ids: proofFileIds } : {}),
          description:
            String(actionModal.comment || '').trim() ||
            t('assistanceManagement.paidDescriptionPrefix', { id: target.id }),
        });
        success(t('assistanceManagement.caseMarkedPaid'));
        await loadAll();
      }
      closeActionModal();
    } catch (err) {
      console.error(err);
      error(toFriendlyAssistanceActionError(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  if (!canRead) {
    return (
      <div className="bg-white border border-red-200 rounded-lg p-4 text-sm text-red-700">
        {t('assistanceManagement.noPermission')}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center bg-gradient-to-b from-[#f2f6fb] to-[#eef3f8] py-16">
        <div className="h-11 w-11 animate-spin rounded-full border-2 border-[#113a5b] border-t-transparent" />
        <p className="mt-4 text-sm text-slate-600">{t('assistanceManagement.loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] space-y-3 sm:space-y-4">
      {canReadOwnCases && memberIdLookupDone && !myMemberProfileId ? (
        <div className="entrea-portal-panel px-4 py-3 sm:px-5 border-l-4 border-l-amber-500 bg-amber-50/90">
          <p className="text-sm text-amber-950">{t('assistanceManagement.memberBanner')}</p>
        </div>
      ) : null}

      <div className={canReadMemberOwn ? 'hidden md:block' : ''}>
        <PageHeader
          title={
            canReadMemberOwn
              ? t('assistanceManagement.pageTitleMember')
              : t('assistanceManagement.pageTitleStaff')
          }
          subtitle={
            canReadMemberOwn
              ? t('assistanceManagement.subtitleMember')
              : t('assistanceManagement.subtitleStaff')
          }
          actions={
            canReadMemberOwn && canCreateOwnCase ? (
              <button
                type="button"
                disabled={submitting || mechanisms.length === 0 || (canReadOwnCases && !myMemberProfileId)}
                onClick={() => {
                  refreshSettings?.();
                  openCreateCaseModal();
                }}
                className="px-4 py-2 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy transition font-medium text-sm whitespace-nowrap disabled:opacity-50 disabled:pointer-events-none disabled:bg-slate-400 disabled:hover:bg-slate-400"
              >
                {t('assistanceManagement.newRequest')}
              </button>
            ) : undefined
          }
        />
      </div>

      {canReadMemberOwn ? (
        <MemberMobileShell>
          <MemberMobileHero
            label={t('assistanceManagement.kickerMember')}
            title={
              instructionQueueCount > 0
                ? t('assistanceManagement.enInstruction', { count: instructionQueueCount })
                : t('assistanceManagement.pageTitleMember')
            }
            hint={
              memberAllocationStats
                ? t('assistanceManagement.dossiersCount', {
                    count: memberAllocationStats.totalCount ?? 0,
                  })
                : undefined
            }
            tone={instructionQueueCount > 0 ? 'warn' : 'info'}
            actions={
              canCreateOwnCase && myMemberProfileId ? (
                <button
                  type="button"
                  disabled={submitting || mechanisms.length === 0}
                  onClick={() => {
                    refreshSettings?.();
                    openCreateCaseModal();
                  }}
                  className="mm-cta flex-1 disabled:opacity-50"
                >
                  {t('assistanceManagement.mobileRequest')}
                </button>
              ) : null
            }
          >
            {(memberAllocationStats?.rejectedCount ?? 0) > 0 ? (
              <p className="mt-2 text-xs font-semibold text-red-700">
                {t('assistanceManagement.rejectedCount', {
                  count: memberAllocationStats.rejectedCount,
                })}
              </p>
            ) : null}
          </MemberMobileHero>
        </MemberMobileShell>
      ) : null}

      {canReadMemberOwn && memberAllocationStats ? (
        <section className="hidden md:grid md:grid-cols-2 md:gap-2.5 xl:grid-cols-4">
          <article className="rounded-2xl bg-white ring-1 ring-slate-200/80 shadow-sm px-3 py-2.5">
            <p className="text-[11px] uppercase tracking-wide text-slate-600">
              {t('assistanceManagement.kpiRequested')}
            </p>
            <p className="mt-1 text-lg font-bold tabular-nums tracking-tight text-[#0f3350]">
              {memberAllocationStats.totalRequested.toLocaleString(localeFmt)}
              <span className="ml-1 align-top text-[10px] font-semibold text-slate-600">FBU</span>
            </p>
          </article>
          <article className="rounded-2xl bg-white ring-1 ring-slate-200/80 shadow-sm px-3 py-2.5 border-l-4 border-emerald-500">
            <p className="text-[11px] uppercase tracking-wide text-emerald-700">
              {t('assistanceManagement.kpiPaidAlloc')}
            </p>
            <p className="mt-1 text-lg font-bold tabular-nums tracking-tight text-[#0f3350]">
              {memberAllocationStats.paidCount}
            </p>
          </article>
          <article className="rounded-2xl bg-white ring-1 ring-slate-200/80 shadow-sm px-3 py-2.5 border-l-4 border-amber-400">
            <p className="text-[11px] uppercase tracking-wide text-amber-800">
              {t('assistanceManagement.kpiInstruction')}
            </p>
            <p className="mt-1 text-lg font-bold tabular-nums tracking-tight text-[#0f3350]">
              {memberAllocationStats.instruction}
            </p>
          </article>
          <article className="rounded-2xl bg-white ring-1 ring-slate-200/80 shadow-sm px-3 py-2.5 border-l-4 border-[#2f6fd4]">
            <p className="text-[11px] uppercase tracking-wide text-slate-600">
              {t('assistanceManagement.kpiWithFiles')}
            </p>
            <p className="mt-1 text-lg font-bold tabular-nums tracking-tight text-[#0f3350]">
              {memberAllocationStats.withFiles}
            </p>
          </article>
        </section>
      ) : null}

      {canReadReports && (
        <UiSurface id="assistance.summary_kpis">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
          <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200/80 shadow-sm border-l-4 border-[#2f6fd4]">
            <p className="text-xs text-slate-600">{t('assistanceManagement.summaryAllocations')}</p>
            <p className="text-lg font-semibold tabular-nums text-[#0f3350]">{Number(summary?.total_cases || 0)}</p>
          </div>
          <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200/80 shadow-sm border-l-4 border-emerald-500">
            <p className="text-xs text-slate-600">{t('assistanceManagement.summaryApproved')}</p>
            <p className="text-lg font-semibold tabular-nums text-[#0f3350]">
              {Number(summary?.total_approved_amount || 0).toLocaleString(localeFmt)}
            </p>
          </div>
          <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200/80 shadow-sm border-l-4 border-[#113a5b]">
            <p className="text-xs text-slate-600">{t('assistanceManagement.summaryPaid')}</p>
            <p className="text-lg font-semibold tabular-nums text-[#0f3350]">
              {Number(summary?.total_paid_amount || 0).toLocaleString(localeFmt)}
            </p>
          </div>
        </div>
        </UiSurface>
      )}

      {(showGovernanceTab || !canReadMemberOwn) && (
      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-3">
        {showGovernanceTab ? (
          <button
            type="button"
            onClick={() => setTab('mechanisms')}
            className={`mm-chip px-3.5 text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4] ${
              tab === 'mechanisms'
                ? 'mm-chip--on shadow-sm'
                : 'mm-chip--off'
            }`}
          >
            {t('assistanceManagement.tabGovernance')}
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => setTab('cases')}
          className={`mm-chip px-3.5 text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4] ${
            tab === 'cases'
              ? 'mm-chip--on shadow-sm'
              : 'mm-chip--off'
          }`}
        >
          {t('assistanceManagement.tabCases')}
        </button>
      </div>
      )}

      {tab === 'mechanisms' && (
        <div className="space-y-3">
          <div className="bg-entrea-panel/60 border border-entrea-sky/30 rounded-lg p-3 text-sm text-entrea-ink">
            {t('assistanceManagement.mechanismIntro')}
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-[#0f3350]">
                {t('assistanceManagement.newMechanismBlockTitle')}
              </p>
              <p className="mt-1 text-xs text-slate-600">{t('assistanceManagement.newMechanismBlockDesc')}</p>
            </div>
            <button
              type="button"
              disabled={!canCreateMechanism || submitting}
              onClick={() => {
                resetMechanismForm();
                setShowNewMechanismModal(true);
              }}
              className="inline-flex shrink-0 items-center justify-center rounded-xl bg-[#113a5b] px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#0f3350] disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4]"
            >
              {t('assistanceManagement.newMechanismBtn')}
            </button>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-3 py-2">{t('assistanceManagement.tableName')}</th>
                  <th className="px-3 py-2">{t('assistanceManagement.tableType')}</th>
                  <th className="px-3 py-2">{t('assistanceManagement.tableEligibility')}</th>
                  <th className="px-3 py-2">{t('assistanceManagement.tableEvents')}</th>
                  <th className="px-3 py-2">{t('assistanceManagement.tableThresholds')}</th>
                  <th className="px-3 py-2">{t('assistanceManagement.tableStatus')}</th>
                  <th className="px-3 py-2">{t('assistanceManagement.tablePeriodicity')}</th>
                  <th className="px-3 py-2">{t('assistanceManagement.tableAction')}</th>
                </tr>
              </thead>
              <tbody>
                {mechanisms.map((m) => (
                  <tr key={m.id} className="border-t border-gray-100">
                    <td className="px-3 py-2">{formatMechanismName(m.name) || '-'}</td>
                    <td className="px-3 py-2">{formatMechanismType(m.type) || '-'}</td>
                    <td className="px-3 py-2 text-xs text-slate-600 max-w-[14rem]">
                      <div className="font-medium text-slate-700">{formatEligibilitySummary(m, t)}</div>
                      {eligibilityFormFromMechanism(m).text ? (
                        <div className="mt-0.5 line-clamp-2 text-slate-500">
                          {eligibilityFormFromMechanism(m).text}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600 max-w-[16rem]">
                      {formatMechanismCategoriesSummary(m, localeFmt, t)}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {(() => {
                        const thr = normalizeCategoryThresholds(m?.category_thresholds);
                        if (thr.length) {
                          const mins = thr.map((r) => r.min_amount).filter((n) => n > 0);
                          const maxs = thr.map((r) => r.max_amount).filter((n) => n > 0);
                          const lo = mins.length
                            ? Math.min(...mins)
                            : Number(m?.assistance_thresholds?.min_amount || 0);
                          const hi = maxs.length
                            ? Math.max(...maxs)
                            : Number(m?.assistance_thresholds?.max_amount || 0);
                          return `${Number(lo).toLocaleString(localeFmt)} - ${Number(hi).toLocaleString(localeFmt)} FBU`;
                        }
                        return `${Number(m?.assistance_thresholds?.min_amount || 0).toLocaleString(localeFmt)} - ${Number(
                          m?.assistance_thresholds?.max_amount || 0
                        ).toLocaleString(localeFmt)} FBU`;
                      })()}
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={m.status || 'active'}
                        disabled={submitting || !canCreateMechanism}
                        onChange={(e) => onSetMechanismStatus(m, e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded-md text-xs"
                      >
                        {statusOptions.map((s) => (
                          <option key={s} value={s}>{labelMechanismStatus(s)}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">{labelMechanismPeriodicity(m.contribution_periodicity)}</td>
                    <td className="px-3 py-2">
                      <TableRowActions
                        onEdit={
                          !submitting && canCreateMechanism
                            ? () => openEditMechanismModal(m)
                            : undefined
                        }
                        onDelete={
                          !submitting && canCreateMechanism
                            ? () => onDeleteMechanism(m)
                            : undefined
                        }
                      />
                    </td>
                  </tr>
                ))}
                {mechanisms.length === 0 && (
                  <tr>
                    <td className="px-3 py-3 text-gray-500" colSpan={8}>
                      {t('assistanceManagement.emptyMechanisms')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
            <div className="mm-card md:hidden divide-y divide-slate-100/90 overflow-hidden">
              {mechanisms.map((m) => (
                <div key={m.id} className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-[#0f3350]">{formatMechanismName(m.name) || '—'}</p>
                      <p className="text-xs text-slate-600 mt-0.5">
                        {t('assistanceManagement.typeLabel')} {formatMechanismType(m.type) || '—'}
                      </p>
                    </div>
                    <span className="shrink-0 px-2 py-1 text-xs font-semibold rounded-full bg-slate-100 text-slate-800 ring-1 ring-slate-200">
                      {labelMechanismStatus(m.status)}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">
                    {t('assistanceManagement.periodicityLabel')} {labelMechanismPeriodicity(m.contribution_periodicity)}
                  </p>
                  <p className="text-xs text-slate-600">
                    {t('assistanceManagement.eligibilityLabel')} {formatEligibilitySummary(m, t)}
                  </p>
                  {eligibilityFormFromMechanism(m).text ? (
                    <p className="text-xs text-slate-500 line-clamp-3">
                      {eligibilityFormFromMechanism(m).text}
                    </p>
                  ) : null}
                  <p className="text-xs text-slate-600">
                    {t('assistanceManagement.eventsLabel')}{' '}
                    {formatMechanismCategoriesSummary(m, localeFmt, t)}
                  </p>
                  <p className="text-xs text-slate-600">
                    {t('assistanceManagement.thresholdsLabel')}{' '}
                    {(() => {
                      const thr = normalizeCategoryThresholds(m?.category_thresholds);
                      if (thr.length) {
                        const mins = thr.map((r) => r.min_amount).filter((n) => n > 0);
                        const maxs = thr.map((r) => r.max_amount).filter((n) => n > 0);
                        const lo = mins.length
                          ? Math.min(...mins)
                          : Number(m?.assistance_thresholds?.min_amount || 0);
                        const hi = maxs.length
                          ? Math.max(...maxs)
                          : Number(m?.assistance_thresholds?.max_amount || 0);
                        return `${Number(lo).toLocaleString(localeFmt)} - ${Number(hi).toLocaleString(localeFmt)} FBU`;
                      }
                      return `${Number(m?.assistance_thresholds?.min_amount || 0).toLocaleString(localeFmt)} - ${Number(
                        m?.assistance_thresholds?.max_amount || 0
                      ).toLocaleString(localeFmt)} FBU`;
                    })()}
                  </p>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <select
                      value={m.status || 'active'}
                      disabled={submitting || !canCreateMechanism}
                      onChange={(e) => onSetMechanismStatus(m, e.target.value)}
                      className="flex-1 min-w-[8rem] px-2 py-2 border border-gray-300 rounded-lg text-xs"
                    >
                      {statusOptions.map((s) => (
                        <option key={s} value={s}>{labelMechanismStatus(s)}</option>
                      ))}
                    </select>
                    <TableRowActions
                      onEdit={
                        !submitting && canCreateMechanism
                          ? () => openEditMechanismModal(m)
                          : undefined
                      }
                      onDelete={
                        !submitting && canCreateMechanism ? () => onDeleteMechanism(m) : undefined
                      }
                    />
                  </div>
                </div>
              ))}
              {mechanisms.length === 0 && (
                <p className="p-4 text-sm text-slate-500 text-center">
                  {t('assistanceManagement.emptyMechanismsMobile')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === 'cases' && (
        <div className="space-y-3">
          {canCreateOwnCase && !canReadMemberOwn ? (
            <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 ring-1 ring-slate-200/80 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-[#0f3350]">
                  {canReadOwnCases
                    ? t('assistanceManagement.demandAllocation')
                    : t('assistanceManagement.nouvelleAllocationStaff')}
                </p>
                <p className="mt-1 text-xs text-slate-600">
                  {canReadOwnCases ? t('assistanceManagement.helpMember') : t('assistanceManagement.helpStaff')}{' '}
                  <span className="font-medium text-[#113a5b]">{t('assistanceManagement.helpSettings')}</span>
                </p>
              </div>
              <div className="flex shrink-0 flex-col items-stretch gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  disabled={
                    submitting ||
                    mechanisms.length === 0 ||
                    (canReadOwnCases && !myMemberProfileId)
                  }
                  onClick={() => {
                    refreshSettings?.();
                    openCreateCaseModal();
                  }}
                  className="inline-flex shrink-0 items-center justify-center rounded-xl bg-[#113a5b] px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#0f3350] disabled:opacity-50 disabled:bg-slate-400 disabled:hover:bg-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4] sm:order-2"
                >
                  {canReadOwnCases
                    ? t('assistanceManagement.faireDemande')
                    : t('assistanceManagement.nouvelleAllocationBtn')}
                </button>
              </div>
            </div>
          ) : null}

          {(!canReadMemberOwn || showMemberAllocationHistorique) && (
          <section
            className={
              canReadMemberOwn
                ? 'entrea-portal-panel px-4 py-4 sm:px-6 sm:py-5 space-y-3'
                : 'space-y-3'
            }
          >
            {canReadMemberOwn && (
              <div className="mb-4">
                <div className="mb-1 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="text-base font-semibold text-entrea-ink">
                      {t('assistanceManagement.historiqueTitle')}
                    </h3>
                    <p className="mt-1 text-sm text-slate-600">{t('assistanceManagement.historiqueHint')}</p>
                  </div>
                  <Link
                    to="/transactionDetails/allocations"
                    className="inline-flex min-h-[40px] shrink-0 items-center justify-center rounded-lg border border-[#bcd4f6] bg-[#edf4ff] px-3 text-sm font-semibold text-[#113a5b] hover:bg-[#e4eefb] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4]"
                  >
                    {t('assistanceManagement.linkTransactions')}
                  </Link>
                </div>
                <div className="mt-3 flex flex-wrap gap-2 md:hidden">
                  <button
                    type="button"
                    onClick={() => {
                      setQueueFilter('');
                      setCaseStatusFilter('');
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4] ${
                      queueFilter === '' && !caseStatusFilter
                        ? 'mm-chip--on'
                        : 'mm-chip--off'
                    }`}
                  >
                    {t('assistanceManagement.chipAll')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setQueueFilter('action');
                      setCaseStatusFilter('');
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4] ${
                      queueFilter === 'action'
                        ? 'mm-chip--on'
                        : 'mm-chip--off'
                    }`}
                  >
                    {t('assistanceManagement.chipQueue', { count: instructionQueueCount })}
                  </button>
                </div>
              </div>
            )}

            <div
              className={
                canReadMemberOwn ? 'hidden md:flex flex-wrap gap-2' : 'flex flex-wrap gap-2'
              }
            >
            <button
              type="button"
              onClick={() => {
                setQueueFilter('');
                setCaseStatusFilter('');
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4] ${
                queueFilter === '' && !caseStatusFilter
                  ? 'mm-chip--on'
                  : 'mm-chip--off'
              }`}
            >
              {t('assistanceManagement.chipAllAllocations')}
            </button>
            <button
              type="button"
              onClick={() => {
                setQueueFilter('action');
                setCaseStatusFilter('');
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4] ${
                queueFilter === 'action'
                  ? 'mm-chip--on'
                  : 'mm-chip--off'
              }`}
            >
              {t('assistanceManagement.chipQueue', { count: instructionQueueCount })}
            </button>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-xs font-medium text-slate-500">
                  {t('assistanceManagement.searchPlaceholder')}
                </p>
                <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                  {filteredCases.length} / {cases.length}
                  {activeFiltersCount > 0 ? ` • ${activeFiltersCount}` : ''}
                </span>
              </div>
              <div className={`grid grid-cols-1 gap-3 ${canReadOwnCases ? 'md:grid-cols-3' : 'md:grid-cols-5'}`}>
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={t('assistanceManagement.searchPlaceholder')}
              className="md:col-span-2 px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4]"
            />
            {!canReadOwnCases ? (
              <>
                <select
                  value={caseStatusFilter}
                  onChange={(e) => {
                    setCaseStatusFilter(e.target.value);
                    if (e.target.value) setQueueFilter('');
                  }}
                  className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4]"
                >
                  <option value="">{t('assistanceManagement.allStatuses')}</option>
                  {caseStatuses.map((s) => (
                    <option key={s} value={s}>{labelCaseStatus(s)}</option>
                  ))}
                </select>
                <select
                  value={caseMechanismFilter}
                  onChange={(e) => setCaseMechanismFilter(e.target.value)}
                  className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4]"
                >
                  <option value="">{t('assistanceManagement.allMechanisms')}</option>
                  {mechanisms.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </>
            ) : null}
            <button
              type="button"
              onClick={() => {
                setSearchText('');
                setCaseStatusFilter('');
                setCaseMechanismFilter('');
                setQueueFilter('');
              }}
              className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4]"
            >
              {t('assistanceManagement.resetFilters')}
            </button>
              </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-700">
                <tr>
                  <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide">{t('assistanceManagement.tableId')}</th>
                  <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide">{t('assistanceManagement.tableMechanism')}</th>
                  {!canReadOwnCases ? (
                    <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide">{t('assistanceManagement.tableMember')}</th>
                  ) : null}
                  <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide">{t('assistanceManagement.newCaseModal.labelEventCategory')}</th>
                  <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide">{t('assistanceManagement.tableStatus')}</th>
                  <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide">{t('assistanceManagement.tableActions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredCases.map((c) => {
                  const memberId = String(c.member_profile_id || '').trim();
                  const memberName = formatAssistanceCaseMemberName(c, memberNameLookup);
                  return (
                  <tr key={c.id} className="border-t border-gray-100 hover:bg-slate-50/60 transition-colors">
                    <td className="px-3 py-2.5 font-mono text-xs">{formatCaseReference(c)}</td>
                    <td className="px-3 py-2.5">
                      {formatMechanismName(mechanismMap[c.mechanism_id]?.name) || c.mechanism_id || '-'}
                    </td>
                    {!canReadOwnCases ? (
                      <td className="px-3 py-2.5">
                        <span
                          className="font-medium text-slate-800"
                          title={memberName || memberId}
                        >
                          {memberName || memberId || '-'}
                        </span>
                        {memberName && memberId ? (
                          <p className="mt-0.5 max-w-[240px] truncate font-mono text-xs text-slate-500">
                            {memberId}
                          </p>
                        ) : null}
                      </td>
                    ) : null}
                    <td className="px-3 py-2.5 text-xs text-slate-600">
                      {formatAssistanceEventCategory(c.event_category, t)}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${caseStatusPillClass(c.status)}`}>
                        {labelCaseStatus(c.status)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-wrap gap-1">
                        <button
                          type="button"
                          className="px-2.5 py-1.5 border rounded-lg text-xs font-medium text-slate-700 hover:bg-slate-50"
                          onClick={() => setSelectedCase(c)}
                        >
                          {t('assistanceManagement.detail')}
                        </button>
                        {c.status === 'draft' && ((canReview && !canReadOwnCases) || memberOwnsCase(c)) && (
                          <button
                            type="button"
                            className="px-3 py-1.5 border border-entrea-sky/40 text-entrea-ink rounded-lg text-xs font-medium hover:bg-entrea-panel"
                            onClick={() => onAction(c, 'submit')}
                          >
                            {t('assistanceManagement.submit')}
                          </button>
                        )}
                        {memberOwnsCase(c) && ['draft', 'submitted', 'in_review'].includes(String(c.status || '').toLowerCase()) && (
                          <button
                            type="button"
                            className="px-2 py-1 border border-[#bcd4f6] rounded text-xs text-[#113a5b] font-medium bg-[#edf4ff]/60 hover:bg-[#e4eefb]"
                            onClick={() => onEditMemberCase(c)}
                          >
                            {t('assistanceManagement.edit')}
                          </button>
                        )}
                        {memberOwnsCase(c) && c.status === 'draft' && (
                          <button
                            type="button"
                            className="px-2 py-1 border border-red-200 text-red-800 rounded text-xs hover:bg-red-50"
                            onClick={() => onDeleteMemberCase(c)}
                          >
                            {t('assistanceManagement.delete')}
                          </button>
                        )}
                        {(c.status === 'submitted' || c.status === 'in_review') && (
                          <>
                            {canApprove && (
                              <button
                                type="button"
                                className="px-3 py-1.5 border border-entrea-sky/40 text-entrea-ink rounded-lg text-xs font-medium hover:bg-entrea-panel"
                                onClick={() =>
                                  openActionModal('approve', c, {
                                    amount: String(Number(c.requested_amount || 0)),
                                    comment: '',
                                  })
                                }
                              >
                                {t('assistanceManagement.approve')}
                              </button>
                            )}
                            {canReject && (
                              <button
                                type="button"
                                className="px-3 py-1.5 border border-entrea-sky/40 text-entrea-ink rounded-lg text-xs font-medium hover:bg-entrea-panel"
                                onClick={() =>
                                  openActionModal('reject', c, {
                                    comment: t('assistanceManagement.rejectManual'),
                                  })
                                }
                              >
                                {t('assistanceManagement.reject')}
                              </button>
                            )}
                          </>
                        )}
                        {c.status === 'approved' && (
                          <>
                            {canPay && (
                              <button
                                type="button"
                                className="px-3 py-1.5 border border-entrea-sky/40 text-entrea-ink rounded-lg text-xs font-medium hover:bg-entrea-panel"
                                onClick={() =>
                                  openActionModal('paid', c, {
                                    amount: String(Number(c.approved_amount || c.requested_amount || 0)),
                                    comment: t('assistanceManagement.paidDescriptionPrefix', { id: c.id }),
                                  })
                                }
                              >
                                {t('assistanceManagement.markPaid')}
                              </button>
                            )}
                            {canClose && (
                              <button type="button" className="px-3 py-1.5 border border-entrea-sky/40 text-entrea-ink rounded-lg text-xs font-medium hover:bg-entrea-panel" onClick={() => onAction(c, 'close')}>
                                {t('assistanceManagement.close')}
                              </button>
                            )}
                          </>
                        )}
                        {(c.status === 'rejected' || c.status === 'paid') && canClose && (
                          <button type="button" className="px-3 py-1.5 border border-entrea-sky/40 text-entrea-ink rounded-lg text-xs font-medium hover:bg-entrea-panel" onClick={() => onAction(c, 'close')}>
                            {t('assistanceManagement.close')}
                          </button>
                        )}
                        {caseStatuses.includes(c.status) ? null : (
                          <span className="text-xs text-gray-500">
                            {t('assistanceManagement.unknownStatus')}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
                {filteredCases.length === 0 && (
                  <tr>
                    <td className="px-3 py-3 text-gray-500" colSpan={canReadOwnCases ? 5 : 6}>
                      {t('assistanceManagement.emptyCases')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>

            <div className="mm-card md:hidden divide-y divide-slate-100/90 overflow-hidden">
              {filteredCases.map((c) => {
                const memberId = String(c.member_profile_id || '').trim();
                const memberName = formatAssistanceCaseMemberName(c, memberNameLookup);
                return (
                <div key={c.id} className="p-4 space-y-3 border-l-4 border-[#2f6fd4] bg-white">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-mono text-xs text-slate-500">{formatCaseReference(c)}</p>
                      <p className="text-sm font-semibold text-[#0f3350] truncate">
                        {formatMechanismName(mechanismMap[c.mechanism_id]?.name) ||
                          t('assistanceManagement.mechanismFallback')}
                      </p>
                      {!canReadOwnCases ? (
                        <p className="text-xs text-slate-700 mt-0.5 truncate">
                          {memberName || memberId || '—'}
                        </p>
                      ) : null}
                      {!canReadOwnCases && memberName && memberId ? (
                        <p className="text-xs text-slate-500 font-mono mt-0.5 truncate">{memberId}</p>
                      ) : null}
                      <p className="text-xs text-slate-600 mt-1">
                        {t('assistanceManagement.newCaseModal.labelEventCategory')}{' '}
                        {formatAssistanceEventCategory(c.event_category, t)}
                      </p>
                    </div>
                    <span className={`shrink-0 px-2 py-1 text-xs font-semibold rounded-full ${caseStatusPillClass(c.status)}`}>
                      {labelCaseStatus(c.status)}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 line-clamp-2">{c.reason || '—'}</p>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      className="px-3 py-2 rounded-lg border border-slate-200 text-xs font-medium text-[#113a5b] bg-[#edf4ff]/50"
                      onClick={() => setSelectedCase(c)}
                    >
                      {t('assistanceManagement.detail')}
                    </button>
                    {c.status === 'draft' && ((canReview && !canReadOwnCases) || memberOwnsCase(c)) && (
                      <button
                        type="button"
                        className="px-3 py-2 rounded-lg border border-slate-200 text-xs font-medium"
                        onClick={() => onAction(c, 'submit')}
                      >
                        {t('assistanceManagement.submit')}
                      </button>
                    )}
                    {memberOwnsCase(c) && ['draft', 'submitted', 'in_review'].includes(String(c.status || '').toLowerCase()) && (
                      <button
                        type="button"
                        className="px-3 py-2 rounded-lg border border-[#bcd4f6] text-xs font-medium text-[#113a5b] bg-white"
                        onClick={() => onEditMemberCase(c)}
                      >
                        {t('assistanceManagement.edit')}
                      </button>
                    )}
                    {memberOwnsCase(c) && c.status === 'draft' && (
                      <button
                        type="button"
                        className="px-3 py-2 rounded-lg border border-red-200 text-xs font-medium text-red-800"
                        onClick={() => onDeleteMemberCase(c)}
                      >
                        {t('assistanceManagement.delete')}
                      </button>
                    )}
                    {(c.status === 'submitted' || c.status === 'in_review') && (
                      <>
                        {canApprove && (
                          <button
                            type="button"
                            className="px-3 py-2 rounded-lg border border-slate-200 text-xs font-medium"
                            onClick={() =>
                              openActionModal('approve', c, {
                                amount: String(Number(c.requested_amount || 0)),
                                comment: '',
                              })
                            }
                          >
                            {t('assistanceManagement.approve')}
                          </button>
                        )}
                        {canReject && (
                          <button
                            type="button"
                            className="px-3 py-2 rounded-lg border border-slate-200 text-xs font-medium"
                            onClick={() =>
                              openActionModal('reject', c, {
                                comment: t('assistanceManagement.rejectManual'),
                              })
                            }
                          >
                            {t('assistanceManagement.reject')}
                          </button>
                        )}
                      </>
                    )}
                    {c.status === 'approved' && (
                      <>
                        {canPay && (
                          <button
                            type="button"
                            className="px-3 py-2 rounded-lg border border-slate-200 text-xs font-medium"
                            onClick={() =>
                              openActionModal('paid', c, {
                                amount: String(Number(c.approved_amount || c.requested_amount || 0)),
                                comment: t('assistanceManagement.paidDescriptionPrefix', { id: c.id }),
                              })
                            }
                          >
                            {t('assistanceManagement.markPaid')}
                          </button>
                        )}
                        {canClose && (
                          <button
                            type="button"
                            className="px-3 py-2 rounded-lg border border-slate-200 text-xs font-medium"
                            onClick={() => onAction(c, 'close')}
                          >
                            {t('assistanceManagement.close')}
                          </button>
                        )}
                      </>
                    )}
                    {(c.status === 'rejected' || c.status === 'paid') && canClose && (
                      <button
                        type="button"
                        className="px-3 py-2 rounded-lg border border-slate-200 text-xs font-medium"
                        onClick={() => onAction(c, 'close')}
                      >
                        {t('assistanceManagement.close')}
                      </button>
                    )}
                  </div>
                </div>
                );
              })}
              {filteredCases.length === 0 && (
                <p className="p-6 text-sm text-slate-500 text-center">
                  {t('assistanceManagement.emptyCasesFiltered')}
                </p>
              )}
            </div>
          </div>

          {selectedCase && (
            <div
              className={
                canReadMemberOwn
                  ? 'entrea-portal-panel border-l-4 border-l-[#2f6fd4] bg-[#f8fbff]/90 px-4 py-4 sm:px-5 sm:py-4 space-y-3'
                  : 'bg-white border border-gray-200 rounded-lg p-4 space-y-3'
              }
            >
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold text-entrea-ink">
                  {t('assistanceManagement.panelTitle', { id: formatCaseReference(selectedCase) })}
                </h3>
                <button
                  type="button"
                  className="shrink-0 text-sm font-medium text-[#113a5b] hover:text-[#0f3350]"
                  onClick={() => setSelectedCase(null)}
                >
                  {t('assistanceManagement.closePanel')}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div>
                  <span className="text-slate-500">{t('assistanceManagement.labelMechanism')}</span>{' '}
                  {formatMechanismName(mechanismMap[selectedCase.mechanism_id]?.name) ||
                    selectedCase.mechanism_id ||
                    '-'}
                </div>
                {!canReadMemberOwn ? (
                  <div>
                    <span className="text-slate-500">{t('assistanceManagement.labelMember')}</span>{' '}
                    {formatAssistanceCaseMemberName(selectedCase, memberNameLookup) ||
                      selectedCase.member_profile_id ||
                      '-'}
                    {selectedCase.member_profile_id &&
                    formatAssistanceCaseMemberName(selectedCase, memberNameLookup) ? (
                      <p className="mt-1 font-mono text-xs text-slate-500">
                        {selectedCase.member_profile_id}
                      </p>
                    ) : null}
                    {selectedCase.member_profile_id &&
                    !formatAssistanceCaseMemberName(selectedCase, memberNameLookup) ? (
                      <button
                        type="button"
                        className="ml-2 text-xs text-[#113a5b] underline hover:no-underline"
                        onClick={() => resolveMemberDisplayName(selectedCase.member_profile_id)}
                        disabled={memberLookupLoading[String(selectedCase.member_profile_id || '').trim()]}
                      >
                        {memberLookupLoading[String(selectedCase.member_profile_id || '').trim()]
                          ? t('assistanceManagement.memberLookupLoading', { defaultValue: 'Recherche…' })
                          : t('assistanceManagement.memberLookupAction', { defaultValue: 'Résoudre' })}
                      </button>
                    ) : null}
                  </div>
                ) : null}
                <div className="md:col-span-1 flex flex-wrap items-center gap-2">
                  <span className="text-slate-500">{t('assistanceManagement.labelStatus')}</span>{' '}
                  <span
                    className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${caseStatusPillClass(
                      selectedCase.status
                    )}`}
                  >
                    {labelCaseStatus(selectedCase.status)}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">{t('assistanceManagement.labelRequested')}</span>{' '}
                  {Number(selectedCase.requested_amount || 0).toLocaleString(localeFmt)} FBU
                </div>
                <div>
                  <span className="text-slate-500">{t('assistanceManagement.labelApproved')}</span>{' '}
                  {Number(selectedCase.approved_amount || 0).toLocaleString(localeFmt)} FBU
                </div>
                <div>
                  <span className="text-slate-500">{t('assistanceManagement.labelPaid')}</span>{' '}
                  {Number(selectedCase.paid_amount || 0).toLocaleString(localeFmt)} FBU
                </div>
                {selectedCase.payment_method ? (
                  <div>
                    <span className="text-slate-500">{t('assistanceManagement.labelPaymentMethod')}</span>{' '}
                    {String(selectedCase.payment_method)}
                  </div>
                ) : null}
              </div>
              <div className="text-sm text-slate-800">
                <span className="text-slate-500">{t('assistanceManagement.labelEventCategory')}</span>{' '}
                {formatAssistanceEventCategory(selectedCase.event_category, t)}
              </div>
              <div className="text-sm text-slate-800">
                <span className="text-slate-500">{t('assistanceManagement.labelReason')}</span>{' '}
                {selectedCase.reason || '—'}
              </div>
              <div className="space-y-2 text-sm text-slate-800">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-slate-500">{t('assistanceManagement.labelFiles')}</span>
                  <span className="text-xs text-slate-500">
                    {Array.isArray(selectedCase.supporting_file_ids) && selectedCase.supporting_file_ids.length
                      ? t('assistanceManagement.filesCount', {
                          count: selectedCase.supporting_file_ids.length,
                        })
                      : t('assistanceManagement.noFiles')}
                  </span>
                </div>
                {Array.isArray(selectedCase.supporting_file_ids) && selectedCase.supporting_file_ids.length ? (
                  <ul className="space-y-1.5 rounded-xl border border-slate-200/90 bg-slate-50/70 px-3 py-2">
                    {selectedCase.supporting_file_ids.map((fid) => {
                      const id = String(fid || '').trim();
                      const meta = caseFileMeta[id] || {};
                      const label = meta.name || id;
                      return (
                        <li key={id} className="flex items-center gap-2 text-sm text-[#0f3350]">
                          <span className="min-w-0 flex-1 truncate font-medium">{label}</span>
                          {meta.url ? (
                            <a
                              href={meta.url}
                              target="_blank"
                              rel="noreferrer"
                              className="shrink-0 text-xs font-bold text-[#113a5b] hover:underline"
                            >
                              {t('assistanceManagement.openFile')}
                            </a>
                          ) : (
                            <span className="shrink-0 text-[11px] text-slate-400">{id.slice(0, 8)}…</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
                {(canReview || canCreateCase || memberOwnsCase(selectedCase)) &&
                ['draft', 'submitted', 'in_review', 'approved', 'paid'].includes(
                  String(selectedCase.status || '').toLowerCase()
                ) ? (
                  <div>
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx"
                      disabled={submitting}
                      onChange={(e) => {
                        onUploadSupportingFiles(selectedCase.id, e.target.files);
                        e.target.value = '';
                      }}
                      className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-entrea-panel file:px-3 file:py-1.5 file:text-entrea-ink"
                    />
                    <p className="mt-1 text-xs text-slate-500">
                      {t('assistanceManagement.attachmentsHint')}
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
          )}

          </section>
          )}
        </div>
      )}

      <ActionFormModal
        isOpen={showNewMechanismModal}
        title={t('assistanceManagement.modalNewMechTitle')}
        onClose={() => {
          if (submitting) return;
          setShowNewMechanismModal(false);
        }}
        onSubmit={onCreateMechanism}
        isSubmitting={submitting}
        submitText={t('assistanceManagement.modalCreateMech')}
        submitDisabled={!String(mechanismForm.name || '').trim()}
        maxWidthClass="max-w-3xl"
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <label className="block text-xs font-medium text-slate-600 space-y-1">
              <span>{t('assistanceManagement.tableName')}</span>
              <input
                value={mechanismForm.name}
                disabled={!canCreateMechanism || submitting}
                onChange={(e) => setMechanismForm((s) => ({ ...s, name: e.target.value }))}
                placeholder={t('assistanceManagement.placeholderMechName')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                autoFocus
              />
            </label>
            <label className="block text-xs font-medium text-slate-600 space-y-1">
              <span>{t('assistanceManagement.tableType')}</span>
              <select
                value={mechanismForm.type}
                disabled={!canCreateMechanism || submitting}
                onChange={(e) => setMechanismForm((s) => ({ ...s, type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                {mechanismTypeOptions.map((typeValue) => (
                  <option key={typeValue} value={typeValue}>
                    {labelMechanismType(typeValue)}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-xs font-medium text-slate-600 space-y-1">
              <span>{t('assistanceManagement.tablePeriodicity')}</span>
              <select
                value={mechanismForm.contribution_periodicity}
                disabled={!canCreateMechanism || submitting}
                onChange={(e) =>
                  setMechanismForm((s) => ({ ...s, contribution_periodicity: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="weekly">{t('assistanceManagement.periodicityWeekly')}</option>
                <option value="monthly">{t('assistanceManagement.periodicityMonthly')}</option>
                <option value="quarterly">{t('assistanceManagement.periodicityQuarterly')}</option>
                <option value="yearly">{t('assistanceManagement.periodicityYearly')}</option>
              </select>
            </label>
            <label className="block text-xs font-medium text-slate-600 space-y-1">
              <span>{t('assistanceManagement.tableStatus')}</span>
              <select
                value={mechanismForm.status}
                disabled={!canCreateMechanism || submitting}
                onChange={(e) => setMechanismForm((s) => ({ ...s, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                {statusOptions.map((s) => (
                  <option key={s} value={s}>{labelMechanismStatus(s)}</option>
                ))}
              </select>
            </label>
          </div>
        </div>
        <MechanismEligibilityEditor
          value={mechanismForm.eligibility}
          disabled={!canCreateMechanism || submitting}
          onChange={(eligibility) => setMechanismForm((s) => ({ ...s, eligibility }))}
        />
        <MechanismCategoryRowsEditor
          rows={mechanismForm.category_rows}
          disabled={!canCreateMechanism || submitting}
          onChange={(category_rows) => setMechanismForm((s) => ({ ...s, category_rows }))}
        />
      </ActionFormModal>

      <ActionFormModal
        isOpen={showEditMechanismModal}
        title={
          editingMechanism
            ? t('assistanceManagement.modalEditMechTitle', {
                name: formatMechanismName(editingMechanism.name) || '',
              })
            : t('assistanceManagement.modalEditMechTitleFallback')
        }
        onClose={() => {
          if (submitting) return;
          setShowEditMechanismModal(false);
          setEditingMechanism(null);
        }}
        onSubmit={onUpdateMechanism}
        isSubmitting={submitting}
        submitText={t('assistanceManagement.submitSave')}
        submitDisabled={!String(mechanismEditForm.name || '').trim()}
        maxWidthClass="max-w-3xl"
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <label className="block text-xs font-medium text-slate-600 space-y-1">
              <span>{t('assistanceManagement.tableName')}</span>
              <input
                value={mechanismEditForm.name}
                disabled={!canCreateMechanism || submitting}
                onChange={(e) => setMechanismEditForm((s) => ({ ...s, name: e.target.value }))}
                placeholder={t('assistanceManagement.placeholderMechName')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                autoFocus
              />
            </label>
            <label className="block text-xs font-medium text-slate-600 space-y-1">
              <span>{t('assistanceManagement.tableType')}</span>
              <select
                value={mechanismEditForm.type}
                disabled={!canCreateMechanism || submitting}
                onChange={(e) => setMechanismEditForm((s) => ({ ...s, type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                {mechanismTypeOptions.map((typeValue) => (
                  <option key={typeValue} value={typeValue}>
                    {labelMechanismType(typeValue)}
                  </option>
                ))}
                {mechanismEditForm.type && !mechanismTypeOptions.includes(String(mechanismEditForm.type).toLowerCase()) ? (
                  <option value={mechanismEditForm.type}>{formatMechanismType(mechanismEditForm.type)}</option>
                ) : null}
              </select>
            </label>
            <label className="block text-xs font-medium text-slate-600 space-y-1">
              <span>{t('assistanceManagement.tablePeriodicity')}</span>
              <select
                value={mechanismEditForm.contribution_periodicity}
                disabled={!canCreateMechanism || submitting}
                onChange={(e) =>
                  setMechanismEditForm((s) => ({ ...s, contribution_periodicity: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="weekly">{t('assistanceManagement.periodicityWeekly')}</option>
                <option value="monthly">{t('assistanceManagement.periodicityMonthly')}</option>
                <option value="quarterly">{t('assistanceManagement.periodicityQuarterly')}</option>
                <option value="yearly">{t('assistanceManagement.periodicityYearly')}</option>
              </select>
            </label>
            <label className="block text-xs font-medium text-slate-600 space-y-1">
              <span>{t('assistanceManagement.tableStatus')}</span>
              <select
                value={mechanismEditForm.status}
                disabled={!canCreateMechanism || submitting}
                onChange={(e) => setMechanismEditForm((s) => ({ ...s, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                {statusOptions.map((s) => (
                  <option key={s} value={s}>{labelMechanismStatus(s)}</option>
                ))}
              </select>
            </label>
          </div>
        </div>
        <MechanismEligibilityEditor
          value={mechanismEditForm.eligibility}
          disabled={!canCreateMechanism || submitting}
          onChange={(eligibility) => setMechanismEditForm((s) => ({ ...s, eligibility }))}
        />
        <MechanismCategoryRowsEditor
          rows={mechanismEditForm.category_rows}
          disabled={!canCreateMechanism || submitting}
          onChange={(category_rows) => setMechanismEditForm((s) => ({ ...s, category_rows }))}
        />
      </ActionFormModal>

      <ActionFormModal
        isOpen={actionModal.isOpen}
        title={
          (actionModal.mode === 'approve' && t('assistanceManagement.actionModalApproveTitle')) ||
          (actionModal.mode === 'reject' && t('assistanceManagement.actionModalRejectTitle')) ||
          (actionModal.mode === 'paid' && t('assistanceManagement.actionModalPaidTitle')) ||
          t('assistanceManagement.actionModalGeneric')
        }
        onClose={closeActionModal}
        onSubmit={submitActionModal}
        isSubmitting={submitting}
        submitDisabled={
          ((actionModal.mode === 'approve' || actionModal.mode === 'reject') &&
            requireDecisionNote &&
            !String(actionModal.comment || '').trim()) ||
          (actionModal.mode === 'paid' && !String(actionModal.payment_method || '').trim())
        }
      >
        {(actionModal.mode === 'approve' || actionModal.mode === 'paid') && (
          <input
            type="number"
            min="0"
            step="0.01"
            value={actionModal.amount}
            onChange={(e) => setActionModal((s) => ({ ...s, amount: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            placeholder={
              actionModal.mode === 'approve'
                ? t('assistanceManagement.placeholderApprovedAmount')
                : t('assistanceManagement.placeholderPaidAmount')
            }
            autoFocus
          />
        )}
        {actionModal.mode === 'paid' && (
          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-600">
              {t('assistanceManagement.paymentMethodLabel')}
            </label>
            <MmSelect
              value={actionModal.payment_method}
              onChange={(payment_method) => setActionModal((s) => ({ ...s, payment_method }))}
              disabled={submitting || ledgerPmLoading}
              required
              placeholder={t('assistanceManagement.paymentMethodPlaceholder')}
              options={paymentMethodOptions}
            />
            {paymentMethodOptions.length === 0 ? (
              <p className="mt-1.5 text-xs text-amber-800">
                {t('assistanceManagement.paymentMethodEmptyHint')}
              </p>
            ) : null}
          </div>
        )}
        {actionModal.mode === 'paid' && (
          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-600">
              {t('assistanceManagement.paymentProofLabel')}
            </label>
            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx"
              disabled={submitting}
              onChange={(e) => {
                const picked = Array.from(e.target.files || []);
                if (!picked.length) return;
                setActionModal((s) => ({
                  ...s,
                  pendingFiles: [...(s.pendingFiles || []), ...picked],
                }));
                e.target.value = '';
              }}
              className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-entrea-panel file:px-3 file:py-1.5 file:text-entrea-ink"
            />
            <p className="mt-1.5 text-xs text-slate-500">{t('assistanceManagement.attachmentsHint')}</p>
            {Array.isArray(actionModal.pendingFiles) && actionModal.pendingFiles.length > 0 ? (
              <ul className="mt-2 space-y-1.5">
                {actionModal.pendingFiles.map((file, index) => (
                  <li
                    key={`${file.name}-${file.size}-${index}`}
                    className="flex items-center gap-2 rounded-lg border border-[#bcd4f6] bg-[#edf4ff]/60 px-3 py-2 text-sm text-[#0f3350]"
                  >
                    <span className="min-w-0 flex-1 truncate font-medium">{file.name}</span>
                    <span className="shrink-0 text-xs text-slate-500">
                      {(Number(file.size) / 1024).toFixed(0)} Ko
                    </span>
                    <button
                      type="button"
                      disabled={submitting}
                      onClick={() =>
                        setActionModal((s) => ({
                          ...s,
                          pendingFiles: (s.pendingFiles || []).filter((_, i) => i !== index),
                        }))
                      }
                      className="shrink-0 text-xs font-bold text-red-700 hover:underline"
                    >
                      {t('assistanceManagement.newCaseModal.removeAttachment')}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        )}
        {(actionModal.mode === 'approve' || actionModal.mode === 'reject' || actionModal.mode === 'paid') && (
          <textarea
            value={actionModal.comment}
            onChange={(e) => setActionModal((s) => ({ ...s, comment: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            placeholder={
              actionModal.mode === 'reject'
                ? t('assistanceManagement.placeholderRejectReason')
                : actionModal.mode === 'approve'
                  ? t('assistanceManagement.placeholderApproveComment')
                  : t('assistanceManagement.placeholderPaidDesc')
            }
          />
        )}
        {(actionModal.mode === 'approve' || actionModal.mode === 'reject') && (
          <p className="text-xs text-slate-500">
            {requireDecisionNote
              ? t('assistanceManagement.decisionNoteRequired')
              : t('assistanceManagement.decisionNoteOptional')}
          </p>
        )}
      </ActionFormModal>

      <AssistanceNewCaseModal
        open={Boolean(showNewCaseModal || editingCase)}
        onClose={() => {
          setShowNewCaseModal(false);
          setEditingCase(null);
        }}
        mechanisms={mechanisms}
        fixedRequestedAmount={Number(settings?.assistance?.default_case_amount ?? 0)}
        canCreate={canReadOwnCases ? canCreateOwnCase : canCreateCase}
        preselectedMemberId={canReadOwnCases ? myMemberProfileId : ''}
        lockMemberSelection={canReadOwnCases}
        onCreated={loadAll}
        editingCase={editingCase}
      />
    </div>
  );
};

export default AssistanceManagement;
